import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, CircularProgress, Stack, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiService from '../services/api';

interface Portal {
  id: string;
  name: string;
  api_key: string | null;
  contact_email: string;
  status: string;
  rate_limit_per_minute: number;
  rate_limit_per_day: number;
  cost_per_search?: number | string;
  created_at: string;
}

const emptyForm = {
  name: '',
  contact_email: '',
  rate_limit_per_minute: 100,
  rate_limit_per_day: 10000,
  cost_per_search: 0.10,
  auth_type: 'both' as 'api-key' | 'username-password' | 'both',
  username: '',
  full_name: '',
};

const PortalManagement: React.FC = () => {
  const [portals, setPortals] = useState<Portal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editPortal, setEditPortal] = useState<Portal | null>(null);
  const [editForm, setEditForm] = useState({ name: '', contact_email: '', rate_limit_per_minute: 100, rate_limit_per_day: 10000, cost_per_search: 0.10 });
  const [newCredentials, setNewCredentials] = useState<any>(null);
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { loadPortals(); }, []);

  const loadPortals = async () => {
    try {
      setLoading(true);
      const data = await apiService.listPortals();
      setPortals(data.portals);
    } catch (error) {
      console.error('Error loading portals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await apiService.createPortal(formData);
      setNewCredentials(result);
      setShowCreate(false);
      setShowCredentials(true);
      setFormData({ ...emptyForm });
      loadPortals();
    } catch (error) {
      console.error('Error creating portal:', error);
      alert('Errore nella creazione del portale');
    }
  };

  const handleDeletePortal = async (id: string, name: string) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il portale "${name}"?`)) return;
    try {
      await apiService.deletePortal(id);
      loadPortals();
    } catch (error) {
      console.error('Error deleting portal:', error);
      alert('Errore nell\'eliminazione del portale');
    }
  };

  const handleRegenerateCredentials = async (id: string, name: string) => {
    if (!window.confirm(`Rigenerare le credenziali per "${name}"? Le vecchie credenziali saranno invalidate.`)) return;
    try {
      const result = await apiService.regenerateCredentials(id);
      setNewCredentials(result);
      setShowCredentials(true);
      loadPortals();
    } catch (error) {
      console.error('Error regenerating credentials:', error);
      alert('Errore nella rigenerazione delle credenziali');
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      await apiService.updatePortal(id, { status: newStatus });
      loadPortals();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Errore nell\'aggiornamento dello status');
    }
  };

  const handleOpenEdit = (portal: Portal) => {
    setEditPortal(portal);
    setEditForm({
      name: portal.name,
      contact_email: portal.contact_email,
      rate_limit_per_minute: portal.rate_limit_per_minute,
      rate_limit_per_day: portal.rate_limit_per_day,
      cost_per_search: portal.cost_per_search != null ? parseFloat(String(portal.cost_per_search)) : 0.10,
    });
    setShowEdit(true);
  };

  const handleUpdatePortal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editPortal) return;
    try {
      await apiService.updatePortal(editPortal.id, editForm);
      setShowEdit(false);
      setEditPortal(null);
      loadPortals();
    } catch (error) {
      console.error('Error updating portal:', error);
      alert('Errore nella modifica del portale');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const statusColor = (s: string): 'success' | 'warning' | 'default' =>
    s === 'active' ? 'success' : s === 'suspended' ? 'warning' : 'default';

  const eur = (n: any) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(parseFloat(n) || 0);

  const needsUser = formData.auth_type === 'username-password' || formData.auth_type === 'both';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Caricamento portali…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4">Gestione Portali</Typography>
          <Typography color="text.secondary">Crea e gestisci i portali client</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)}>
          Crea Nuovo Accesso
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>API Key</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rate Limit (min/day)</TableCell>
              <TableCell align="right">Costo/ricerca</TableCell>
              <TableCell>Creato il</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {portals.map((portal) => (
              <TableRow key={portal.id} hover>
                <TableCell><strong>{portal.name}</strong></TableCell>
                <TableCell>{portal.contact_email}</TableCell>
                <TableCell>
                  {portal.api_key ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {portal.api_key.substring(0, 16)}…
                      </Typography>
                      <Tooltip title="Copia API key">
                        <IconButton size="small" onClick={() => copyToClipboard(portal.api_key!)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Username/Password
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={portal.status} color={statusColor(portal.status)} size="small" />
                </TableCell>
                <TableCell>{portal.rate_limit_per_minute} / {portal.rate_limit_per_day}</TableCell>
                <TableCell align="right">{eur(portal.cost_per_search)}</TableCell>
                <TableCell>{new Date(portal.created_at).toLocaleDateString('it-IT')}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    {portal.status === 'active' ? (
                      <Tooltip title="Sospendi">
                        <IconButton size="small" color="warning" onClick={() => handleUpdateStatus(portal.id, 'suspended')}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Attiva">
                        <IconButton size="small" color="success" onClick={() => handleUpdateStatus(portal.id, 'active')}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Modifica">
                      <IconButton size="small" onClick={() => handleOpenEdit(portal)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rigenera credenziali">
                      <IconButton size="small" onClick={() => handleRegenerateCredentials(portal.id, portal.name)}>
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton size="small" color="error" onClick={() => handleDeletePortal(portal.id, portal.name)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Portal Dialog */}
      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreatePortal}>
          <DialogTitle>Crea Nuovo Accesso</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField select label="Tipo di accesso" fullWidth value={formData.auth_type}
                onChange={(e) => setFormData({ ...formData, auth_type: e.target.value as any })}
                helperText="Scegli cosa creare: il resto del modulo si adatta di conseguenza">
                <MenuItem value="api-key">Portale (API Key + Secret)</MenuItem>
                <MenuItem value="username-password">Utente (Username + Password)</MenuItem>
                <MenuItem value="both">Entrambi (Portale + Utente)</MenuItem>
              </TextField>

              <TextField label="Nome" fullWidth required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <TextField label="Email Contatto" type="email" fullWidth required value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField label="Rate Limit (per minuto)" type="number" fullWidth value={formData.rate_limit_per_minute}
                  onChange={(e) => setFormData({ ...formData, rate_limit_per_minute: parseInt(e.target.value) })} />
                <TextField label="Rate Limit (per giorno)" type="number" fullWidth value={formData.rate_limit_per_day}
                  onChange={(e) => setFormData({ ...formData, rate_limit_per_day: parseInt(e.target.value) })} />
              </Stack>
              <TextField label="Costo per ricerca (€)" type="number" fullWidth value={formData.cost_per_search}
                onChange={(e) => setFormData({ ...formData, cost_per_search: parseFloat(e.target.value) })}
                slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                helperText="Prezzo addebitato al cliente per ogni ricerca (il margine = prezzo − nostro costo)" />

              {needsUser && (
                <>
                  <TextField label="Username Utente" fullWidth required value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    helperText="Verrà creato un utente associato a questo accesso" />
                  <TextField label="Nome Completo Utente" fullWidth value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
                  <Alert severity="info">La password verrà generata automaticamente e mostrata dopo la creazione.</Alert>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button type="submit" variant="contained">Crea Accesso</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Portal Dialog */}
      <Dialog open={showEdit && !!editPortal} onClose={() => setShowEdit(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleUpdatePortal}>
          <DialogTitle>Modifica Portale</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField label="Nome Portale" fullWidth required value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              <TextField label="Email Contatto" type="email" fullWidth required value={editForm.contact_email}
                onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField label="Rate Limit (per minuto)" type="number" fullWidth value={editForm.rate_limit_per_minute}
                  onChange={(e) => setEditForm({ ...editForm, rate_limit_per_minute: parseInt(e.target.value) })} />
                <TextField label="Rate Limit (per giorno)" type="number" fullWidth value={editForm.rate_limit_per_day}
                  onChange={(e) => setEditForm({ ...editForm, rate_limit_per_day: parseInt(e.target.value) })} />
              </Stack>
              <TextField label="Costo per ricerca (€)" type="number" fullWidth value={editForm.cost_per_search}
                onChange={(e) => setEditForm({ ...editForm, cost_per_search: parseFloat(e.target.value) })}
                slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                helperText="Prezzo addebitato al cliente per ogni ricerca" />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button type="submit" variant="contained">Salva Modifiche</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentials && !!newCredentials} onClose={() => setShowCredentials(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Credenziali Portale</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Queste credenziali saranno mostrate solo una volta. Salvale in un luogo sicuro.
          </Alert>
          <Stack spacing={2}>
            <TextField label="Nome Portale" fullWidth value={newCredentials?.portal?.name || ''} slotProps={{ input: { readOnly: true } }} />
            {newCredentials?.portal?.api_key && newCredentials?.portal?.api_secret && (
              <>
                <Typography variant="subtitle2">Credenziali API</Typography>
                <CopyField label="API Key" value={newCredentials.portal.api_key} onCopy={copyToClipboard} />
                <CopyField label="API Secret" value={newCredentials.portal.api_secret} onCopy={copyToClipboard} />
              </>
            )}
            {newCredentials?.user && (
              <>
                <Typography variant="subtitle2">Credenziali Utente</Typography>
                <CopyField label="Username" value={newCredentials.user.username} onCopy={copyToClipboard} />
                <CopyField label="Password" value={newCredentials.user.password} onCopy={copyToClipboard} />
                <TextField label="Email" fullWidth value={newCredentials.user.email || ''} slotProps={{ input: { readOnly: true } }} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setShowCredentials(false)}>Ho Salvato le Credenziali</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CopyField: React.FC<{ label: string; value: string; onCopy: (v: string) => void }> = ({ label, value, onCopy }) => (
  <TextField
    label={label}
    fullWidth
    value={value}
    slotProps={{
      input: {
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Copia">
              <IconButton size="small" onClick={() => onCopy(value)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      },
    }}
  />
);

export default PortalManagement;
